import { Loader } from "@mantine/core";
import { BackTop, Table, Card, Row, Col, Statistic } from "antd";
import React, { useState, useEffect } from "react";
import ConsultationFilterBar from "../../../components/filter-bars/consultations-report-filters";
import { calcAge, exportToExcel } from "../../../helpers/utilities";
import { useDownloadExcel } from "react-export-table-to-excel";
import { format } from "date-fns";

export default function ConsultationsReport({ data, isLoading }) {
  const [filteredData, setFilteredData] = useState([]);

  const handleReset = () => {
    setFilteredData(data);
  };

  const handleApplyFilter = (newData) => {
    setFilteredData(newData);
  };

  // const { onDownload: exportToEXcel } = useDownloadExcel( {
  //     currentTableRef: document.getElementById( 'consultationsReportTable' ),
  //     filename: 'Consultation Report',
  //     sheet: 'main'
  // } );

  useEffect(() => {
    setFilteredData(data);
  }, [data]);

  // Calculate metrics for the current filtered data
  const calculateMetrics = () => {
    const totalConsultations = filteredData?.length;
    const completedConsultations = filteredData?.filter(
      (consultation) => consultation?.isComplete
    ).length;
    const pendingConsultations = totalConsultations - completedConsultations;
    const totalPrescriptions = filteredData?.reduce(
      (sum, consultation) => sum + (consultation?.prescriptions?.length || 0),
      0
    );
    const totalLabs = filteredData?.reduce(
      (sum, consultation) => sum + (consultation?.laboratories?.length || 0),
      0
    );
    const completionRate =
      totalConsultations > 0
        ? (completedConsultations / totalConsultations) * 100
        : 0;

    return {
      totalConsultations,
      completedConsultations,
      pendingConsultations,
      totalPrescriptions,
      totalLabs,
      completionRate,
    };
  };

  const columns = [
    {
      title: "#",
      // sorter: ( a, b ) => a.insuranceNumber > b.insuranceNumber,
      sortDirections: ["descend", "ascend"],
      render: (text, record, index) => ++index,
    },
    {
      title: "code",
      sorter: (a, b) => a?.code > b?.code,
      dataIndex: "code",
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Date - Time",
      sorter: (a, b) => a?.createdAt > b?.createdAt,
      sortDirections: ["descend", "ascend"],
      render: (text, record, index) =>
        record?.createdAt
          ? format(new Date(record?.createdAt), "dd MMM, yyyy hh:mm a")
          : "-",
    },
    {
      title: "Patient",
      sorter: (a, b) => a?.firstName > b?.lastName,
      sortDirections: ["descend", "ascend"],
      render: (text, record, index) =>
        record?.patient
          ? `${record?.patient?.firstName}  ${record?.patient?.lastName}`
          : "-",
    },
    {
      title: "Gender",
      dataIndex: ["patient", "gender"],
      sorter: (a, b) => a?.patient?.gender > b?.patient?.gender,
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Age",
      // sorter: ( a, b ) => a.contact > b.contact,
      sortDirections: ["descend", "ascend"],
      render: (text, record, index) => calcAge(record?.patient?.dob).age,
    },

    {
      title: "Type",
      sorter: (a, b) => a?.type > b?.type,
      dataIndex: "type",
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Doctor",
      sorter: (a, b) => a?.doctor?.firstName > b?.doctor?.firstName,
      sortDirections: ["descend", "ascend"],
      render: (text, record, index) =>
        record?.doctor
          ? `${record?.doctor?.firstName} ${record?.doctor?.lastName}`
          : "-",
    },
    {
      title: "Status",
      sorter: (a, b) => a?.isComplete > b?.isComplete,
      sortDirections: ["descend", "ascend"],
      render: (text, record, index) =>
        record?.isComplete ? "Complete" : "Pending",
    },
    {
      title: "Meds",
      sorter: (a, b) => a?.prescriptions?.length > b?.prescriptions?.length,
      sortDirections: ["descend", "ascend"],
      render: (text, record, index) => record?.prescriptions?.length,
    },
    {
      title: "Labs",
      sorter: (a, b) => a?.laboratories > b?.laboratories,
      sortDirections: ["descend", "ascend"],
      render: (text, record, index) => record?.laboratories?.length,
    },
  ];

  const metrics = calculateMetrics();

  return (
    <div>
      <ConsultationFilterBar
        data={data}
        excelExport={() =>
          exportToExcel(
            "consultationsReportTable",
            "rivia-consultations-report"
          )
        }
        onReset={handleReset}
        onFilter={(d) => handleApplyFilter(d)}
      />

      {/* Metrics Cards */}
      <Row gutter={16} className="mt-3 mb-3">
        <Col xs={24} sm={12} md={6} lg={4}>
          <Card>
            <Statistic
              title="Total Consultations"
              value={metrics.totalConsultations}
              valueStyle={{ color: "#3f8600" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6} lg={4}>
          <Card>
            <Statistic
              title="Completed"
              value={metrics.completedConsultations}
              valueStyle={{ color: "#3f8600" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6} lg={4}>
          <Card>
            <Statistic
              title="Pending"
              value={metrics.pendingConsultations}
              valueStyle={{ color: "#faad14" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6} lg={4}>
          <Card>
            <Statistic
              title="Total Prescriptions"
              value={metrics.totalPrescriptions}
              valueStyle={{ color: "#1890ff" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6} lg={4}>
          <Card>
            <Statistic
              title="Total Labs"
              value={metrics.totalLabs}
              valueStyle={{ color: "#722ed1" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6} lg={4}>
          <Card>
            <Statistic
              title="Completion Rate"
              value={metrics.completionRate.toFixed(1)}
              suffix="%"
              valueStyle={{
                color:
                  metrics.completionRate >= 80
                    ? "#3f8600"
                    : metrics.completionRate >= 60
                    ? "#faad14"
                    : "#cf1322",
              }}
            />
          </Card>
        </Col>
      </Row>

      {/* advanced filters
            -> date range
            -> doctor
            -> status
            -> age
            
            */}
      {/* { isLoading &&
                <div className='mt-3' >
                    <Spin /> <span>refreshing...</span>
                </div>
            } */}
      <Table
        className="border mt-3"
        rowKey={(record) => record.id}
        dataSource={filteredData}
        columns={columns}
        loading={isLoading}
        bordered
        id="consultationsReportTable"
      />
      <BackTop />
    </div>
  );
}
